import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { queryKeys } from '~/api/queryKeys';
import { refreshAuthToken } from '~/api/refresh-token';
import { useAuthStore } from '~/store/auth.store';

import {
  createWorkflowHistoryStream,
  type HistoryStreamFrame,
} from '../services/workflow-history-stream';
import { getWorkflowHistoryPage } from '../services/workflow-inspection.service';
import type { HistoryEvent } from '../types/workflow-inspection.types';
import { lastEventId, mergeHistoryEvents } from '../utils/history-events';

import type { WorkflowHistoryData } from './use-workflow-history';

/**
 * How the live tail is doing: `live` (streaming), `degraded` (server says
 * Temporal is briefly unreachable, still retrying), `polling` (stream gave up,
 * fetching new events on an interval), `closed` (execution finished — nothing
 * more will arrive), `idle` (not running / not enabled).
 */
export type HistoryStreamStatus =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'degraded'
  | 'polling'
  | 'closed';

const MAX_RECONNECT_ATTEMPTS = 4;
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 8000;
const RATE_LIMIT_CAP_MS = 30_000;
/** A connection open this long earns a fresh reconnect budget. */
const MIN_STABLE_CONNECTION_MS = 10_000;
/** Fallback poll cadence once the stream has given up. */
const POLL_INTERVAL_MS = 10_000;

/** ±50% jitter so disconnected clients don't reconnect in lockstep. */
function jitter(ms: number): number {
  return ms * (0.5 + Math.random());
}

function statusOf(err: unknown): number | undefined {
  return (err as { status?: number }).status;
}

/** Append raw events into the cached history (no-op until the initial load landed). */
function appendEvents(
  queryClient: QueryClient,
  workflowId: string,
  runId: string | null,
  events: HistoryEvent[],
): void {
  if (events.length === 0) return;
  queryClient.setQueryData<WorkflowHistoryData>(
    queryKeys.workflowInspection.history(workflowId, runId),
    (prev) => (prev ? { ...prev, events: mergeHistoryEvents(prev.events, events) } : prev),
  );
}

interface Options {
  workflowId: string;
  runId: string | null;
  /** Caller passes "execution RUNNING and initial history loaded". */
  enabled: boolean;
}

/**
 * Owns the live history tail (§3.2) for one execution:
 *   - connects from the last held eventId, appending each (raw) event into the
 *     `useWorkflowHistory` cache entry;
 *   - reconnects on `closed: streamLimit|degraded` and on network errors (capped,
 *     jittered backoff; one token refresh on 401/403; honours 429 Retry-After),
 *     then falls back to polling `afterEventId` every 10 s;
 *   - stops on `closed: workflowClosed|notFound` and refreshes the describe read
 *     so status/result update;
 *   - Freeze holds incoming events in a buffer (count exposed) and releases them
 *     on unfreeze, so the table doesn't shift under someone reading it.
 */
export function useWorkflowHistoryStream({ workflowId, runId, enabled }: Options) {
  const queryClient = useQueryClient();
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));
  const executionKey = `${workflowId}:${runId ?? ''}`;
  // Keyed by execution so a previous run's status never leaks into a new one.
  const [state, setState] = useState<{ key: string; status: HistoryStreamStatus }>({
    key: executionKey,
    status: 'idle',
  });
  const [frozen, setFrozenState] = useState(false);
  const [bufferedCount, setBufferedCount] = useState(0);
  const frozenRef = useRef(false);
  const bufferRef = useRef<HistoryEvent[]>([]);

  useEffect(() => {
    if (!enabled || !isAuthed || !workflowId) return;

    const historyKey = queryKeys.workflowInspection.history(workflowId, runId);
    let cancelled = false;
    // Read through a function: `cancelled` flips in other callbacks across awaits,
    // which control-flow narrowing can't see.
    const isCancelled = () => cancelled;
    let attempts = 0;
    let authRefreshed = false;
    let openedAt: number | null = null;
    let controller: AbortController | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const setStatus = (status: HistoryStreamStatus) => {
      if (!cancelled) setState({ key: executionKey, status });
    };
    const cursor = () => {
      const held = queryClient.getQueryData<WorkflowHistoryData>(historyKey)?.events ?? [];
      const buffered = bufferRef.current;
      return lastEventId(buffered.length > 0 ? mergeHistoryEvents(held, buffered) : held);
    };
    const deliver = (events: HistoryEvent[]) => {
      if (events.length === 0) return;
      if (frozenRef.current) {
        bufferRef.current = bufferRef.current.concat(events);
        setBufferedCount(bufferRef.current.length);
      } else {
        appendEvents(queryClient, workflowId, runId, events);
      }
    };
    const finish = () => {
      cancelled = true;
      controller?.abort();
      setState({ key: executionKey, status: 'closed' });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workflowInspection.describe(workflowId, runId),
      });
    };

    // Fallback once the stream has given up: page new events every 10 s.
    const poll = async () => {
      if (cancelled) return;
      try {
        let token: string | null = null;
        do {
          const page = await getWorkflowHistoryPage(workflowId, {
            runId,
            afterEventId: cursor(),
            nextPageToken: token,
            pageSize: 1000,
          });
          deliver(page.events);
          token = page.nextPageToken;
        } while (token && !isCancelled());
      } catch {
        // Transient — the next tick retries; a closed run is caught by describe polling.
      }
      if (!isCancelled()) timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    };

    const scheduleReconnect = (delayMs?: number) => {
      if (cancelled) return;
      if (attempts >= MAX_RECONNECT_ATTEMPTS) {
        setStatus('polling');
        timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
        return;
      }
      attempts += 1;
      const base = Math.min(MAX_RECONNECT_DELAY_MS, BASE_RECONNECT_DELAY_MS * 2 ** (attempts - 1));
      setStatus('connecting');
      timer = setTimeout(connect, delayMs ?? jitter(base));
    };

    const onFrame = (frame: HistoryStreamFrame) => {
      if (frame.kind === 'event') {
        deliver([frame.event]);
        setStatus('live');
        return;
      }
      const { type, reason } = frame.frame;
      if (type === 'degraded') setStatus('degraded');
      else if (type === 'heartbeat') setStatus('live');
      else if (reason === 'workflowClosed' || reason === 'notFound') finish();
      // `streamLimit` / `degraded` closes: reconnect from the cursor when the body ends.
    };

    function connect() {
      if (cancelled) return;
      controller = createWorkflowHistoryStream({
        workflowId,
        runId,
        afterEventId: cursor(),
        onOpen: () => {
          openedAt = Date.now();
          setStatus('live');
        },
        onFrame,
        onComplete: () => {
          if (cancelled) return;
          if (openedAt !== null && Date.now() - openedAt >= MIN_STABLE_CONNECTION_MS) attempts = 0;
          openedAt = null;
          scheduleReconnect();
        },
        onError: (err) => {
          if (cancelled) return;
          if (openedAt !== null && Date.now() - openedAt >= MIN_STABLE_CONNECTION_MS) attempts = 0;
          openedAt = null;
          const status = statusOf(err);
          if (status === 404) {
            finish();
            return;
          }
          if (status === 429) {
            const hint = (err as { retryAfterSeconds?: number }).retryAfterSeconds;
            scheduleReconnect(
              hint !== undefined ? Math.min(hint * 1000, RATE_LIMIT_CAP_MS) : undefined,
            );
            return;
          }
          if ((status === 401 || status === 403) && !authRefreshed) {
            authRefreshed = true;
            void refreshAuthToken().then((outcome) => {
              if (cancelled) return;
              if (outcome === 'refreshed') connect();
              else if (outcome === 'transient') scheduleReconnect();
            });
            return;
          }
          scheduleReconnect();
        },
      });
    }

    connect();
    return () => {
      cancelled = true;
      controller?.abort();
      if (timer) clearTimeout(timer);
    };
  }, [enabled, isAuthed, workflowId, runId, executionKey, queryClient]);

  /** Freeze/unfreeze the table; unfreezing releases everything buffered meanwhile. */
  function setFrozen(next: boolean) {
    frozenRef.current = next;
    setFrozenState(next);
    if (!next && bufferRef.current.length > 0) {
      appendEvents(queryClient, workflowId, runId, bufferRef.current);
      bufferRef.current = [];
      setBufferedCount(0);
    }
  }

  const status: HistoryStreamStatus = !enabled
    ? 'idle'
    : state.key === executionKey
      ? state.status
      : 'connecting';
  return { status, frozen, setFrozen, bufferedCount };
}
